import React from 'react';
import { NavLink } from 'react-router-dom';
import { BelowMapSetter, useBelowMap } from '../layouts/BelowMapContext';
import { BelowMapTab } from '../layouts/BelowMapTab';
import { ScenarioList } from './ScenarioList';
import { useGetScenarioGroupsQuery } from '../services/edgeApi';
import { useEffect, useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@iconicicons/react';
import { useViewMode } from '../hooks/useViewMode';
import { DevelopmentFlag } from '../helpers/DevelopmentFlag';

export function Scenarios() {
  const [viewMode] = useViewMode();
  const { data } = useGetScenarioGroupsQuery(undefined, {
    skip: viewMode !== 'development',
  });
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const { setOpen: setBelowMapOpen } = useBelowMap();

  useEffect(() => {
    setBelowMapOpen(true);
  }, []);

  const groupsList = useMemo(
    () =>
      data?.flatMap((group) => [
        <tr key={group.id}>
          <td>
            <NavLink to={`/scenarios/groups/${group.id}`}>{group.name}</NavLink>
          </td>
          <td>{group.description}</td>
          <td
            className={openGroups[group.id] ? 'table-info' : ''}
            role="button"
            onClick={() =>
              setOpenGroups((prev) => ({
                ...prev,
                [group.id]: !prev[group.id],
              }))
            }
          >
            {group.scenarios.length} scenarios{' '}
            <span>
              {openGroups[group.id] ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </span>
          </td>
        </tr>,

        ...[
          openGroups[group.id] ? (
            <tr key={`${group.id}#children`}>
              <td colSpan={3}>
                <table className="table table-info">
                  <thead>
                    <tr>
                      <th>Scenario</th>
                      <th>Flow Date</th>
                      <th>Created By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.scenarios.map((scenario) => (
                      <tr key={`${group.id}#${scenario.id}`}>
                        <td>
                          <NavLink
                            to={`/scenarios/groups/${group.id}/scenarios/${scenario.id}`}
                          >
                            {scenario.name}
                          </NavLink>
                        </td>
                        <td>
                          {new Date(scenario.flow_date).toLocaleDateString()}
                        </td>
                        <td>{scenario.creator ?? '--'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </td>
            </tr>
          ) : (
            []
          ),
        ],
      ]),
    [data, openGroups],
  );

  const scenraioGroupsTabName = useMemo(
    () => (
      <>
        <DevelopmentFlag className="me-2" />
        Scenario Groups
      </>
    ),
    [],
  );

  const belowMapTabs = useMemo(() => {
    const tabs = [
      <BelowMapTab
        key="below-map-scenarios"
        contextKey="scenarios"
        tabName="Scenarios"
      >
        <ScenarioList key="below-map-scenarios-list" />
      </BelowMapTab>,
    ];
    const devTabs = [
      <BelowMapTab
        key="below-map-scenario-groups"
        contextKey="scenario-groups"
        tabName={scenraioGroupsTabName}
      >
        <table className="table">
          <thead>
            <tr>
              <th>Group</th>
              <th>Description</th>
              <th>Scenarios</th>
            </tr>
          </thead>
          <tbody>{groupsList}</tbody>
        </table>
      </BelowMapTab>,
    ];
    return viewMode === 'development' ? [...tabs, ...devTabs] : tabs;
  }, [groupsList, scenraioGroupsTabName, viewMode]);

  return (
    <>
      <h3>Scenarios</h3>
      {/* TOOD: this and other components should be more reusable - for instance, we should be able to use this to show lists of scenarios wherever we want, without necessarily having a header - maybe Scenarios.js should just grab the scenario list raw, and then have a ScenarioPage.js that adds the header or whatever else. */}
      <div className="d-grid gap-2">
        <NavLink
          className="btn btn-success btn-block"
          to="/scenarios/next_day/path-report"
        >
          Next Day Scenario
        </NavLink>
        <NavLink className="btn btn-success btn-block" to="/scenarios/new">
          Create Custom Scenario
        </NavLink>
        {viewMode === 'development' && (
          <NavLink
            className="btn btn-success btn-block"
            to="/scenarios/groups/new"
          >
            <DevelopmentFlag className="me-2" />
            Create New Scenario Group
          </NavLink>
        )}
      </div>
      <BelowMapSetter>{belowMapTabs}</BelowMapSetter>
    </>
  );
}
